import type { Contact } from '@/modules/contact';
import { ContactLogic } from '@/modules/contact';
import type {
  MessageDraft,
  MessageIntent,
  SharingLegDetails,
} from '@/modules/message';
import { INTENT_SYMBOL } from '@/modules/share/logic';
import { INTENT_NOUN } from '@/modules/template';
import type {
  OutboxItem,
  OutboxPrimaryAction,
  OutboxRowStatus,
  OutboxSend,
} from './types';

/**
 * Stands in for the leg id in a queued message's body. A leg is created only
 * when its message is first opened, so the body is composed ahead of time with
 * this in the leg link's place and the real id is put in at handoff. Plain
 * letters and dashes, so it survives being written into a URL path untouched.
 */
export const OUTBOX_LEG_ID_PLACEHOLDER = 'outbox-leg-id';

/** Names a group row spells out before counting the rest. */
const NAMED_IN_GROUP_ROW = 5;

const STATUS_LABEL: Record<OutboxRowStatus, string> = {
  sent: 'sent',
  toConfirm: 'did it send?',
  upNext: 'up next',
  ready: '',
};

const SHEET_TITLE: Record<OutboxRowStatus, string> = {
  sent: 'Sent',
  toConfirm: 'Did It Send?',
  upNext: 'Up Next',
  ready: 'Not Sent',
};

function withItem(
  send: OutboxSend,
  index: number,
  change: (item: OutboxItem) => OutboxItem
): OutboxSend {
  return {
    ...send,
    items: send.items.map((item, i) => (i === index ? change(item) : item)),
  };
}

/** The first `queued` item at or after `from`, wrapping round to the start. */
function firstQueuedFrom(items: readonly OutboxItem[], from: number): number {
  const count = items.length;
  for (let step = 0; step < count; step++) {
    const index = (from + step) % count;
    if (items[index].state === 'queued') return index;
  }
  return -1;
}

export const OutboxLogic = {
  /**
   * An item's id: its intent and who it reaches. A recipient is on one message
   * only, so no two items in a send share one.
   */
  key(draft: MessageDraft): string {
    return `${draft.header.intent}:${draft.header.recipients
      .map(contact => contact.localId)
      .join(',')}`;
  },

  /** An outbox item for one draft of a share being queued. */
  itemFor(
    draft: MessageDraft,
    bodyTemplate: string,
    leg: SharingLegDetails
  ): OutboxItem {
    return {
      id: OutboxLogic.key(draft),
      recipients: draft.header.recipients,
      body: draft.body,
      bodyTemplate,
      leg,
      state: 'queued',
    };
  },

  /** A new send, every message queued and the first one up next. */
  newSend(args: {
    id: string;
    topicId: string;
    topicTitle: string;
    queuedAt: string;
    items: OutboxItem[];
  }): OutboxSend {
    return { ...args, resumeAt: 0 };
  },

  /**
   * The message an item sends: its composed body with the leg's id in place,
   * or, before the leg exists, the body as the composer previewed it.
   */
  messageFor(item: OutboxItem): string {
    return item.legId
      ? item.bodyTemplate.split(OUTBOX_LEG_ID_PLACEHOLDER).join(item.legId)
      : item.body;
  },

  /**
   * Who a row is for: the person's name for one recipient, and for a group
   * message the first few informal names with a count of the rest —
   * "Jordan, Mom, Dev, Ana, Priya +5".
   */
  rowName(recipients: readonly Contact[]): string {
    if (recipients.length === 1) {
      return ContactLogic.displayName(recipients[0]);
    }
    const named = recipients
      .slice(0, NAMED_IN_GROUP_ROW)
      .map(contact => ContactLogic.informalName(contact));
    const rest = recipients.length - named.length;
    return rest > 0 ? `${named.join(', ')} +${rest}` : named.join(', ');
  },

  /** The short name the confirm prompt and its buttons use. */
  shortName(recipients: readonly Contact[]): string {
    if (recipients.length === 1) {
      return ContactLogic.displayName(recipients[0]);
    }
    return `this group of ${recipients.length}`;
  },

  /** The line under a group message's names: "Group text · 10 people". */
  groupLabel(recipients: readonly Contact[]): string {
    return `Group text · ${recipients.length} people`;
  },

  /** Whether any message in the send goes to more than one person. */
  hasGroupTexts(send: OutboxSend): boolean {
    return send.items.some(item => item.recipients.length > 1);
  },

  /** How many people the send reaches, all messages together. */
  peopleCount(send: OutboxSend): number {
    return send.items.reduce((n, item) => n + item.recipients.length, 0);
  },

  /**
   * How many messages have gone. A resend in progress still counts: it was sent
   * before it was opened again.
   */
  sentCount(send: OutboxSend): number {
    return send.items.filter(
      item =>
        item.state === 'sent' ||
        (item.state === 'awaitingConfirm' && item.resending)
    ).length;
  },

  /** How many people the sent messages reached. */
  sentPeopleCount(send: OutboxSend): number {
    return send.items
      .filter(item => item.state === 'sent')
      .reduce((n, item) => n + item.recipients.length, 0);
  },

  /**
   * The send and message a leg was created for, or null when no message in the
   * outbox went out on it. A leg in the outbox is one the sender sent, so
   * finding it tells their own tap on its link apart from a recipient's.
   */
  findLeg(
    sends: readonly OutboxSend[],
    legId: string
  ): { send: OutboxSend; index: number } | null {
    for (const send of sends) {
      const index = send.items.findIndex(item => item.legId === legId);
      if (index !== -1) return { send, index };
    }
    return null;
  },

  /** The message handed to the SMS app and waiting on "did it send?", or -1. */
  awaitingConfirmIndex(send: OutboxSend): number {
    return send.items.findIndex(item => item.state === 'awaitingConfirm');
  },

  /**
   * The message the outbox opens next, or -1 when none is left: the first
   * queued message from where the sender left off, wrapping round so a message
   * marked not sent or set aside to share later comes round again.
   */
  upNextIndex(send: OutboxSend): number {
    if (send.items.length === 0) return -1;
    return firstQueuedFrom(send.items, send.resumeAt % send.items.length);
  },

  /** Every row's status, in list order. */
  rowStatuses(send: OutboxSend): OutboxRowStatus[] {
    const upNext = OutboxLogic.upNextIndex(send);
    return send.items.map((item, index): OutboxRowStatus => {
      switch (item.state) {
        case 'sent':
          return 'sent';
        case 'awaitingConfirm':
          return 'toConfirm';
        default:
          return index === upNext ? 'upNext' : 'ready';
      }
    });
  },

  /**
   * A status in words, for the row's right-hand column. A ready row's is
   * empty: the gray ring already says it is waiting its turn.
   */
  statusLabel(status: OutboxRowStatus): string {
    return STATUS_LABEL[status];
  },

  /**
   * The preview sheet's title: the message's intent, symbol and name, and
   * where the row stands — "👋 Intro · Did It Send?".
   */
  sheetTitle(intent: MessageIntent, status: OutboxRowStatus): string {
    return `${INTENT_SYMBOL[intent]} ${INTENT_NOUN[intent].one} · ${SHEET_TITLE[status]}`;
  },

  /** What the main button does, given where the send stands. */
  primaryAction(send: OutboxSend): OutboxPrimaryAction {
    const awaitingConfirm = OutboxLogic.awaitingConfirmIndex(send);
    const next = OutboxLogic.upNextIndex(send);
    if (awaitingConfirm !== -1) {
      return next === -1
        ? { kind: 'confirmFinish', index: awaitingConfirm }
        : { kind: 'confirmNext', index: awaitingConfirm, next };
    }
    if (next === -1) return { kind: 'done' };
    // Started once any message has been opened, even if it went back in the
    // queue to share later.
    const started = send.items.some(
      item => item.state !== 'queued' || item.legId != null
    );
    return started
      ? { kind: 'continue', index: next }
      : { kind: 'start', index: next };
  },

  // ── changes ───────────────────────────────────────────────────────────────
  // Each returns the send as it stands afterwards. None of them settles a
  // message that is awaiting confirmation on another's behalf: the outbox asks
  // about that one first (see `open`).

  /** The leg a message was created on. */
  withLeg(send: OutboxSend, index: number, legId: string): OutboxSend {
    return withItem(send, index, item => ({ ...item, legId }));
  },

  /**
   * The message at `index` has been handed to the SMS app. Opening a queued
   * message moves the sender's place past it; opening a sent one again is
   * a resend, which leaves their place alone. Opening the message that is
   * already waiting on the sender is a retry of the same handoff.
   */
  open(send: OutboxSend, index: number): OutboxSend {
    const state = send.items[index]?.state;
    // Opening again the message already waiting on the sender changes nothing.
    if (state === 'awaitingConfirm') return send;
    const resending = state === 'sent';
    const opened = withItem(send, index, item => ({
      ...item,
      state: 'awaitingConfirm',
      resending,
    }));
    return resending ? opened : { ...opened, resumeAt: index + 1 };
  },

  /** The sender says the message went. */
  confirm(send: OutboxSend, index: number): OutboxSend {
    return withItem(send, index, item => ({
      ...item,
      state: 'sent',
      resending: false,
    }));
  },

  /**
   * The sender will share the awaiting message later. A first send goes back
   * in the queue where it is, and the sender's place stays past it, so the
   * messages after it come first and it comes round again once they are done.
   * A resend goes back to sent, since it went the first time.
   */
  shareLater(send: OutboxSend, index: number): OutboxSend {
    return withItem(send, index, item => ({
      ...item,
      state: item.resending ? 'sent' : 'queued',
      resending: false,
    }));
  },

  /**
   * Whether a message can be marked sent by hand: only one that has been opened
   * in Messages, since a message never opened never carried its link anywhere.
   */
  canMarkSent(item: OutboxItem): boolean {
    return item.legId != null && item.state !== 'sent';
  },

  /** Mark an opened message sent without confirming it from the list. */
  markSent(send: OutboxSend, index: number): OutboxSend {
    const item = send.items[index];
    if (!item || !OutboxLogic.canMarkSent(item)) return send;
    return OutboxLogic.confirm(send, index);
  },

  /** Put a message back in the queue. */
  markNotSent(send: OutboxSend, index: number): OutboxSend {
    return withItem(send, index, item => ({
      ...item,
      state: 'queued',
      resending: false,
    }));
  },

  /** Take a message out of the send altogether. */
  remove(send: OutboxSend, index: number): OutboxSend {
    return {
      ...send,
      items: send.items.filter((_, i) => i !== index),
      resumeAt: index < send.resumeAt ? send.resumeAt - 1 : send.resumeAt,
    };
  },

  /** Whether any message is left that Mark All as Sent would change. */
  canMarkAllSent(send: OutboxSend): boolean {
    return send.items.some(OutboxLogic.canMarkSent);
  },

  /**
   * Every opened message marked sent. Messages never opened stay where they
   * are: nothing of theirs has gone out.
   */
  markAllSent(send: OutboxSend): OutboxSend {
    return {
      ...send,
      items: send.items.map(item =>
        item.legId == null ? item : { ...item, state: 'sent', resending: false }
      ),
    };
  },

  /** Every message back in the queue, the list started over. */
  markAllNotSent(send: OutboxSend): OutboxSend {
    return {
      ...send,
      items: send.items.map(item => ({
        ...item,
        state: 'queued',
        resending: false,
      })),
      resumeAt: 0,
    };
  },

  /** Whether any message in the send has yet to go. */
  hasUnsent(send: OutboxSend): boolean {
    return send.items.some(item => item.state !== 'sent');
  },

  /** A topic's send while it still has messages to go, or null. */
  unsentSendFor(
    sends: readonly OutboxSend[],
    topicId: string
  ): OutboxSend | null {
    return (
      sends.find(
        send => send.topicId === topicId && OutboxLogic.hasUnsent(send)
      ) ?? null
    );
  },

  /**
   * The send to show when the outbox opens without being asked for one: the
   * newest with anything left to send, or else the newest.
   */
  defaultSend(sends: readonly OutboxSend[]): OutboxSend | null {
    const newestFirst = [...sends].sort((a, b) =>
      b.queuedAt.localeCompare(a.queuedAt)
    );
    return newestFirst.find(OutboxLogic.hasUnsent) ?? newestFirst[0] ?? null;
  },

  /**
   * The sends with a new one queued: a topic keeps one send in the outbox, so
   * queuing a share for a topic replaces the one before it.
   */
  withSendQueued(sends: readonly OutboxSend[], send: OutboxSend): OutboxSend[] {
    return [send, ...sends.filter(other => other.topicId !== send.topicId)];
  },
};
